import { Box, Collapse, Divider, Drawer, Grid, IconButton, List, ListItem, ListItemButton } from '@mui/material';
import { useLazyQuery, useMutation } from '@apollo/client';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import moment from 'moment';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import './sidebar.scss';
import { GET_PREVIOUS_VERSION, GET_VERSION, REMOVE_VERSION_NAME, RESTORE_PREVIOUS_VERSION, UPDATE_VERSION_NAME } from '../../apis/version';
import Button from '../atoms/button';
import ProjectContext from '../../context/ProjectContext';
import useLoader from '../../context/LoaderContext';
import useProjectVersionHistory from '../../context/VersionHistoryContext';

const VersionHistoryDrawerPropTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const StyledMenuItem = styled(MenuItem)` && { line-height: 16.9px; } `;

const groupVersionsByDate = (versions) => {
  const groupedVersions = {};
  versions.forEach((item) => {
    const dateKey = moment(item?.createdAt, 'x').format('YYYY-MM-DD');
    if (!groupedVersions[dateKey]) {
      groupedVersions[dateKey] = [];
    }
    groupedVersions[dateKey].push(item);
  });
  return groupedVersions;
};

const VersionHistoryDrawer = (props) => {
  // init
  const { open, onClose } = props;
  const { isLoading, setIsLoading } = useLoader();
  const { selectedListItem, setReadOnlyMode, setSelectedListItem, setShowVersionHistoryBar, setIsCurrentVersion } = useProjectVersionHistory();
  const { projectId } = useContext(ProjectContext);

  // states
  const [fetch, setFetch] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openItems, setOpenItems] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [versionName, setVersionName] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewVersion, setViewVersion] = useState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);

  // constants
  const textInputRef = useRef(null);
  const groupedVersions = useMemo(() => groupVersionsByDate(viewVersion?.getVersions || []), [viewVersion]);

  // mutations/queries
  const [removeVersionName] = useMutation(REMOVE_VERSION_NAME);
  const [updateVersionName] = useMutation(UPDATE_VERSION_NAME);
  const [getVersion] = useLazyQuery(GET_VERSION, { fetchPolicy: 'network-only', variables: { input: { projectId: parseInt(projectId, 10) } } });
  const [previousVersion] = useLazyQuery(GET_PREVIOUS_VERSION, { fetchPolicy: 'network-only' });
  const [restorePreviousVersion] = useLazyQuery(RESTORE_PREVIOUS_VERSION);

  const hideRiskBar = () => {
    onClose();
  };

  const handleCollapse = (dateKey) => {
    setOpenItems((prevOpenItems) => ({
      ...prevOpenItems,
      [dateKey]: !prevOpenItems[dateKey],
    }));
  };

  const handleItemClick = async (item) => {
    setSelectedItemId(item?.id);
    if (item.id !== selectedItemId) {
      if (!isLoading) setIsLoading(true);
      setReadOnlyMode(true);
      if (selectedItemId !== item?.id) setVersionName(false);
      await previousVersion({ variables: { input: { id: item?.id, projectId: parseInt(projectId, 10), showVersion: true } } });
      setSelectedListItem(item);
    }
  };

  const handleTextFieldChange = async (event) => {
    if (event.key === 'Enter') {
      const variables = { input: { id: selectedItem?.id, name: event.target.value, projectId: parseInt(projectId, 10) } };
      await updateVersionName({ variables });
      setSelectedListItem({ ...selectedListItem, name: event.target.value });
      setFetch(!fetch);
      setSelectedItemId(selectedItemId);
    }
    if (event.key === 'Escape') {
      setVersionName(false);
    }
  };

  const renderContent = (item) => {
    if (versionName && editingItem === item) {
      const defaultText = item?.name === null ? moment(item?.createdAt, 'x').format('MMM DD, h:mm A') : item?.name;
      return (
        <div style={{ paddingLeft: '16px', paddingBottom: '10px' }}>
          <input className="version-name" onKeyDown={handleTextFieldChange} defaultValue={defaultText} ref={textInputRef} />
        </div>
      );
    }

    if (item.name === null) {
      return (
        <ListItem className="nameListItem">
          {moment(item?.createdAt, 'x').format('MMM DD, h:mm A')}
        </ListItem>
      );
    }
    return <ListItem className="nameListItem">{item?.name}</ListItem>;
  };

  const calculateMargin = (item) => {
    if (item?.currentVersion && item?.name !== null) { return '65px'; }
    if (item?.currentVersion) { return '45px'; }
    if (item?.name !== null) { return '40px'; }
    return '19px';
  };

  const handleClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
    setSelectedItemId(item?.id);
    setVersionName(false);
    setFetch(false);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleRestore = (e) => {
    e.stopPropagation();
    setAnchorEl(null);
    setOpenDialog(true);
  };

  const handleName = (e) => {
    e.stopPropagation();
    setVersionName(true);
    setEditingItem(selectedItem);
    setAnchorEl(null);

    setTimeout(() => {
      if (textInputRef.current) {
        textInputRef.current.focus();
      }
    }, 0);
  };

  const handleRemoveName = async (e) => {
    e.stopPropagation();
    const variables = { input: { id: selectedItem?.id, projectId: parseInt(projectId, 10), removeVersionName: true } };
    setAnchorEl(null);
    await removeVersionName({ variables });
    setSelectedListItem({ ...selectedListItem, name: null });
    setFetch(!fetch);
  };

  const dialogHandleClose = () => {
    setOpenDialog(false);
  };

  const dialogHandleClickRestore = async () => {
    if (!isLoading) setIsLoading(true);
    setOpenDialog(false);
    const variables = { input: { id: selectedListItem?.id, projectId: parseInt(projectId, 10) } };
    await restorePreviousVersion({ variables });
    setShowVersionHistoryBar(false);
    setIsCurrentVersion(true);
    setSelectedListItem(null);
  };

  useEffect(() => {
    const versionData = async () => {
      try {
        const { data, loading } = await getVersion();
        if (!loading && data) {
          setViewVersion(data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setVersionName(false);
      }
    };
    versionData();
  }, [fetch]);

  return (
    <div>
      <Grid item className="transition-class">
        <Drawer className="version-sidebar" variant="permanent" anchor="right" open={open} onClose={onClose}>
          <Grid container alignItems="center">
            <Grid item xs={11} className="versionTitle"> Version History </Grid>
            <Grid item xs={1}>
              <CloseOutlinedIcon className="versionCloseButton" onClick={hideRiskBar} />
            </Grid>
          </Grid>
          <div className="scrollable-content">
            {viewVersion?.getVersions?.length > 0 ? (
              Object.keys(groupedVersions).map((dateKey) => (
                <div key={dateKey}>
                  <List disablePadding className="headerList">
                    <ListItemButton style={{ background: 'none', paddingLeft: '0px' }} disableRipple onClick={() => handleCollapse(dateKey)}>
                      {openItems[dateKey] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                      <ListItem className="titleList">
                        {moment(dateKey).calendar(null, { sameDay: '[Today]', lastDay: '[Yesterday]', lastWeek: 'MMM D, YYYY', sameElse: 'MMM D, YYYY' })}
                      </ListItem>
                    </ListItemButton>
                    <Divider sx={{ mt: '5px' }} />
                  </List>
                  <List className="headerList">
                    <Collapse in={openItems[dateKey]} timeout="auto" unmountOnExit>
                      {groupedVersions[dateKey].map((item) => (
                        <div key={item.createdAt} style={{ paddingBottom: '12px' }}>
                          <ListItemButton
                            disableTouchRipple
                            selected={selectedItemId === item.id}
                            onClick={() => handleItemClick(item)}
                            className="listItemButton"
                          >
                            <Box sx={{ width: '360px' }}>
                              {renderContent(item)}
                              {item?.name !== null && (
                                <ListItem className="dateListItem">
                                  {item && moment(item.createdAt, 'x').format('MMM DD, h:mm A')}
                                </ListItem>
                              )}
                              {item?.currentVersion && (<ListItem className="currentListItem">Current version</ListItem>)}
                              <ListItem
                                className="createdByListItem"
                                secondaryAction={
                                  <>
                                    <IconButton
                                      size="small"
                                      style={{
                                        marginBottom: calculateMargin(item),
                                      }}
                                      disableRipple
                                      onClick={(event) => {
                                        handleClick(event, item);
                                      }}
                                    >
                                      <MoreVertIcon />
                                    </IconButton>
                                    <Menu
                                      id="basic-menu"
                                      anchorEl={selectedItemId === item.id ? anchorEl : null}
                                      keepMounted
                                      open={selectedItemId === item.id ? !!anchorEl : false}
                                      onClose={(e) => { handleClose(e); }}
                                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                      transition="true"
                                    >
                                      {[
                                        !selectedItem?.currentVersion && (
                                          <StyledMenuItem key={`restore-${item.createdAt}`} onClick={(e) => { handleRestore(e); }}> Restore this version </StyledMenuItem>
                                        ),
                                        selectedItem?.name !== null && (
                                          <div>
                                            <StyledMenuItem key={`rename-${item.createdAt}`} onClick={(e) => { handleName(e); }}> Rename </StyledMenuItem>
                                            <StyledMenuItem key={`remove-name-${item.createdAt}`} onClick={(e) => { handleRemoveName(e); }}> Remove name </StyledMenuItem>
                                          </div>
                                        ),
                                        selectedItem?.name === null && (
                                          <StyledMenuItem key={`name-${item.createdAt}`} onClick={(e) => { handleName(e); }}> Name this version </StyledMenuItem>
                                        ),
                                      ]}
                                    </Menu>
                                  </>
                                }
                              >
                                {`${item.user?.firstName} ${item.user?.lastName}`}
                              </ListItem>
                            </Box>
                          </ListItemButton>
                        </div>
                      ))}
                    </Collapse>
                  </List>
                </div>
              ))
            ) : (
              <>
                <Divider sx={{ mt: '20px' }} />
                <List>
                  {viewVersion?.getVersions?.length === 0 ? (
                    <ListItem key="empty-list" className="emptyList">
                      &quot;Nothing to review. Go forth and make history!&quot;
                    </ListItem>
                  ) : null}
                </List>
              </>
            )}
          </div>
        </Drawer>
      </Grid>
      <span>
        {openDialog && (
          <Dialog
            className="custom-modal-dialog confirmation-modal-dialog"
            open={openDialog}
            onClose={dialogHandleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogContent className="dialog-content">
              <DialogContentText id="alert-dialog-description">
                {`Are you sure you want to restore ${selectedListItem?.name || moment(selectedListItem?.createdAt, 'x').format('MMM DD, h:mm A')} ?`}
              </DialogContentText>
            </DialogContent>
            <DialogActions className="dialog-actions">
              <Button primary outlined onClick={dialogHandleClose} disableFocusRipple> Cancel </Button>
              <Button primary onClick={dialogHandleClickRestore} autoFocus disableFocusRipple> Restore </Button>
            </DialogActions>
          </Dialog>
        )}
      </span>
    </div>
  );
};

VersionHistoryDrawer.propTypes = VersionHistoryDrawerPropTypes;
export default VersionHistoryDrawer;
